<template>
  <div :class="className">
    <div class="vertical-center horizontal-center">
      <div class="client-id">
        <div class="profile-picture" :style="'background-image: url(' + profileImage +')'">
          <img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=">
        </div>
        <template v-if="isValidShop">
          <h2>{{ companyName }}</h2>
          <div class="tab-content">
            <div class="login-form">
              <template v-if="authState === 'initial'">
                <div class="alert alert-warning login-error" v-if="sessionExpired">{{ _('Your session has expired. Please log in again.') }}</div>
                <div class="alert alert-danger login-error" v-if="loginError">{{ loginError }}</div>
                <div class="login-social-buttons">
                  <div class="login-social-button">
                    <GoogleSignInButton @click="loginWithGoogle" />
                  </div>
                  <div class="login-social-button">
                    <MicrosoftSignInButton @click="loginWithMicrosoft" />
                  </div>
                </div>
                <div class="login-divider">
                  <span>{{ _('or') }}</span>
                </div>
                <button type="button" class="btn btn-success btn-lg" v-on:click="showEmailOTPForm">
                  <i class="fa fa-envelope"></i> <span class="d-none d-md-inline">{{ _('Continue with Email') }}</span>
                </button>
              </template>
              <div v-else-if="authState === 'email-otp'">
                <p>{{ _('Enter your email address and we\'ll send you a verification code to login.') }}</p>
                <div class="login-otp-form">
                  <form v-on:submit.prevent="requestOTPCode">
                    <div class="form-group">
                      <label>{{ _('Email') }}</label>
                      <input class="form-control" type="email" v-model="emailOTPForm.email" required autocomplete="email" ref="email" />
                    </div>
                    <div class="alert alert-danger login-error" v-if="loginError">{{ loginError }}</div>
                    <div class="form-group">
                      <button type="submit" class="btn btn-success btn-lg" :disabled="isRequestingOTP">
                        <i class="fa fa-spinner fa-spin" v-if="isRequestingOTP"></i>
                        <template v-else>{{ _('Send Verification Code') }}</template>
                      </button>
                    </div>
                  </form>
                </div>
                <a href="javascript:;" v-on:click="backToInitial">{{ _('Back to other options') }}</a>
              </div>

              <div v-else-if="authState === 'email-otp-verify'">
                <div class="alert alert-info">
                  <p>{{ _('If your email is registered for this shop, you will receive a code.') }}</p>
                  <p class="mb-0">{{ _('We\'ve sent a verification code to:') }} <strong>{{ emailOTPForm.email }}</strong></p>
                </div>
                <form v-on:submit.prevent="verifyOTPCode">
                  <div class="form-group">
                    <label>{{ _('Verification Code') }}</label>
                    <input class="form-control" type="tel" pattern="[0-9]*" v-model="emailOTPCode" required maxlength="6" autocomplete="one-time-code" />
                  </div>
                  <div class="alert alert-danger login-error" v-if="loginError">{{ loginError }}</div>
                  <div class="form-group">
                    <button type="submit" class="btn btn-success btn-lg" :disabled="isVerifyingOTP">
                      <i class="fa fa-spinner fa-spin" v-if="isVerifyingOTP"></i>
                      <template v-else>{{ _('Verify and Login') }}</template>
                    </button>
                  </div>
                </form>
                <div class="login-resend-code">
                  <a href="javascript:;" v-on:click="requestOTPCode">{{ _('Didn\'t receive the code? Send again') }}</a>
                  <p class="login-resend-hint">{{ _('A new code replaces any previous one.') }}</p>
                  <hr>
                  <a href="javascript:;" v-on:click="backToInitial">{{ _('Back to other options') }}</a>
                </div>
              </div>

              <div v-else-if="authState === 'staff-reason'">
                <template v-if="staffEmail">
                  <p>{{ _('You are logged in with email:') }} <strong>{{ staffEmail }}</strong></p>
                </template>
                <p>{{ _('Please select a reason for accessing this shop.') }}</p>
                <form v-on:submit.prevent="submitStaffReason">
                  <div class="form-group">
                    <label>{{ _('Reason') }}</label>
                    <select class="form-control" v-model="staffReasonForm.reason" required>
                      <option value="">{{ _('Select a reason') }}</option>
                      <option v-for="preset in staffReasonPresets" :key="preset.id" :value="preset.id">
                        {{ staffReasonLabel(preset) }}
                      </option>
                    </select>
                  </div>
                  <div class="form-group" v-if="staffReasonForm.reason === 'other'">
                    <label>{{ _('Details') }}</label>
                    <textarea class="form-control" v-model="staffReasonForm.detail" rows="3" required></textarea>
                  </div>
                  <div class="alert alert-danger login-error" v-if="loginError">{{ loginError }}</div>
                  <div class="button-row flex-row">
                    <button type="submit" class="btn btn-success btn-lg" :disabled="isSubmittingStaffReason">
                      <i class="fa fa-spinner fa-spin" v-if="isSubmittingStaffReason"></i>
                      <template v-else>{{ _('Continue') }}</template>
                    </button>
                    <button type="button" class="btn btn-default btn-lg" v-on:click="backToInitial">{{ _('Back') }}</button>
                  </div>
                </form>
              </div>

              <div class="login-privacy text-center" style="margin-top: 20px">
                <a :href="privacyPolicyUrl" target="_blank" rel="noopener noreferrer">{{ _('Privacy policy') }}</a>
              </div>
            </div>
          </div>
          <div class="flex-row space-between footer-row">
            <div class="identifier text-center" v-if="shopId"><span>{{ _('Shop ID') }}:</span> <strong>{{ shopId }}</strong></div>
            <div class="identifier text-center" :title="_('Client identifier')" v-if="clientid"><i class="fa fa-fw fa-computer"></i> {{ clientid }} <button class="btn btn-default btn-sm" style="padding: 2px 12px; margin-top: -2px" v-on:click="copyClientIdToClipboard"><i class="fa fa-fw fa-copy"></i></button></div>
            <div class="identifier text-center"><button class="btn btn-default btn-sm" type="button" v-on:click="reloadApp"><i class="fa fa-fw fa-arrows-rotate"></i> {{ _('Reload') }}</button></div>
          </div>
        </template>
        <div v-else class="tab-content">
          <h2>{{ _('No shop exists with this identifier.') }}</h2>
          <div class="form-group">
            <input class="form-control" style="font-size: 16pt; font-weight: bold; text-align: center" type="text" v-model="triedShopId" name="shopid" ref="shopid" v-on:keyup.enter="reloadUI"/>
          </div>
          <p><strong>{{ _('Check that the shop identifier you typed is correct.') }}</strong><br />{{ _('If not, input the correct identifier and press reload.') }}</p>
          <p>{{ _('This message may also be shown if settings could not be loaded, in which case reloading the user interface will likely help.') }}</p>
          <p><button class="btn btn-success" style="margin-top: 10px" v-on:click="reloadUI"><i class="fa fa-fw fa-arrows-rotate"></i> {{ _('Reload') }}</button></p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.button-row {
  display: flex;
  flex-direction: row;
  gap: 10px;
  justify-content: center;
}
.login-resend-code {
  text-align: center;
  margin-top: 15px;
}
.login-resend-hint {
  margin: 8px 0 0;
  color: #888;
  font-size: 13px;
}
.footer-row {
  padding-top: 20px;
  gap: 10px;
  @media (max-width: 768px) {
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
}
.vertical-center {
  display: flex;
  align-items: center;
  flex-direction: row;
  background-color: #F4F6F6;
}
.horizontal-center {
  justify-content: center;
}
input {
  text-align: center;
}
.login-social-buttons {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}
.login-error {
  margin-bottom: 15px;
}
.login-otp-form {
  margin-bottom: 10px;
}
.login-divider {
  text-align: center;
  margin-top: 10px;
  margin-bottom: 15px;
  color: #888;
}
.login-otp-hint {
  margin: 16px 0 12px;
  padding: 12px 14px;
  font-size: 14px;
  line-height: 1.45;
  color: #4a5f6d;
  background-color: #f4f6f8;
  border-radius: 4px;
  border-left: 3px solid #507C97;
}
h2 {
  margin-top: 20px;
  margin-bottom: 30px;
  color: #507C97;
  font-size: 32px;
}
.tab-content {
  border-radius: 2px;
  border: 1px solid #FFF;
  padding: 30px 30px;
  padding-bottom: 15px;
  background-color: #FFF;
  box-shadow: 1px 1px 3px rgba(0,0,0,0.1);
}
.profile-picture {
  display: inline-block;
  min-width: 100px;
  max-width: 350px;
  width: 33%;
  border-radius: 50%;
  margin-bottom: 10px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
}
.profile-picture img {
  width: 100%;
  height: auto;
}
@media (max-width: 991px) {
  .vertical-center {
    margin-left: -15px;
    margin-right: -15px;
    padding-left: 15px;
    padding-right: 15px;
    padding-top: 30px;
  }
}
</style>

<script>
import { mapGetters } from 'vuex'
import errors from '~/lib/errors'
import settingshelpers from '~/lib/settingshelpers'
import { createShopAuthClient } from '~/lib/auth-client'
import { currentPosUiHash, savePosReturnHash } from '~/lib/loginReturnHash'
import { clearStaffReasonFlow, isStaffReasonFlowActive, markStaffReasonFlow, reloadApp, SESSION_EXPIRED_STORAGE_KEY } from '~/lib/sessionRecovery'

const OTP_STORAGE_KEY = 'johku_otp_flow'
const OTP_TTL = 900_000

export default {
  setup (props) {
    const config = useRuntimeConfig()
    const authClient = createShopAuthClient(props.shopId)
    const session = authClient.useSession()
    usePosAuthTokenSync(session)
    return { authClient, session, config }
  },
  data () {
    return {
      // Never trust the persisted staff-reason flag to pick the initial screen:
      // it can only be confirmed once we know who this visitor actually is (see
      // mounted() / finishLogin()), so anyone else sharing this browser tab never
      // sees the staff-only "select a reason" screen before that confirmation.
      authState: 'initial',
      emailOTPForm: { email: '' },
      emailOTPCode: '',
      staffReasonForm: { reason: '', detail: '' },
      staffReasonPresets: [],
      loginError: '',
      isRequestingOTP: false,
      isVerifyingOTP: false,
      isSubmittingStaffReason: false,
      customShopId: null
    }
  },
  mixins: [errors, settingshelpers],
  computed: {
    triedShopId: {
      get () {
        return this.customShopId !== null ? this.customShopId : this.shopId
      },
      set (newValue) {
        this.customShopId = newValue
      }
    },
    profileImage () {
      return this.settings.profile_id ? this.baseDomain + this.shopId + '/files/' + this.settings.profile_id + '.' + this.settings.profile_ext : '/img/johku-profile.svg'
    },
    settings () {
      return this.shopSettings(this.shopId)
    },
    isValidShop () {
      return this.settings && this.settings.id
    },
    companyName () {
      return this.settings.companyname
    },
    sessionExpired () {
      return this.$store.state.sessionExpired
    },
    clientid () {
      if (this.$store && this.$store.state && this.$store.state.clientid) {
        return this.$store.state.clientid
      }
      return false
    },
    privacyPolicyUrl () {
      if (this.locale === 'fi_FI') {
        return this.config.public.privacyPolicyUrlFi || 'https://johku.fi/fi/privacy'
      }
      return this.config.public.privacyPolicyUrlEn || 'https://johku.fi/en/privacy'
    },
    staffEmail () {
      // Options API unwraps the session ref on `this`, while the raw ref still
      // has `.value`. Accept either shape so OTP and social sign-in both resolve.
      const state = this.session?.value?.data ? this.session.value : this.session
      const sessionEmail = state?.data?.user?.email
      if (sessionEmail) {
        return sessionEmail
      }
      return this.emailOTPForm.email || ''
    },
    ...mapGetters(['baseDomain', 'localDomain', 'linkedStores', 'allSettings', 'locale', 'apiKey'])
  },
  props: ['className', 'component', 'components', 'shopId'],
  methods: {
    showEmailOTPForm () {
      this.authState = 'email-otp'
      this.loginError = ''
      this.$nextTick(() => this.focusEmail())
    },
    saveOtpFlow () {
      if (typeof window === 'undefined') return
      sessionStorage.setItem(OTP_STORAGE_KEY, JSON.stringify({
        email: this.emailOTPForm.email,
        savedAt: Date.now()
      }))
    },
    restoreOtpFlow () {
      if (typeof window === 'undefined') return null
      try {
        const raw = sessionStorage.getItem(OTP_STORAGE_KEY)
        if (!raw) return null
        const saved = JSON.parse(raw)
        if (Date.now() - saved.savedAt > OTP_TTL) {
          sessionStorage.removeItem(OTP_STORAGE_KEY)
          return null
        }
        return saved
      } catch {
        return null
      }
    },
    clearOtpFlow () {
      if (typeof window === 'undefined') return
      sessionStorage.removeItem(OTP_STORAGE_KEY)
    },
    backToInitial () {
      this.clearOtpFlow()
      this.authState = 'initial'
      this.loginError = ''
    },
    showAccessDeniedError () {
      this.clearOtpFlow()
      clearStaffReasonFlow()
      this.loginError = this._('You do not have access to this shop. Please contact your shop administrator if you need access.')
      this.authState = 'initial'
    },
    hasPendingStaffReason () {
      return isStaffReasonFlowActive(this.shopId)
    },
    // Re-attempts the silent login for this shop instead of leaving the user
    // stuck behind the generic "session expired" overlay. Deliberately does
    // NOT switch to the 'staff-reason' screen here - that only happens once
    // finishLogin() gets a confirmed STAFF_REASON_REQUIRED from the backend,
    // so this is safe to call speculatively for anyone.
    startStaffReasonFlow () {
      markStaffReasonFlow(this.shopId)
      this.$store.dispatch('setSessionExpired', false)
      this.$store.dispatch('setUser', null)
      this.$store.dispatch('setApiKey', null)
      this.loginError = ''
      this.$nextTick(() => {
        this.finishLogin()
      })
    },
    buildSocialState () {
      savePosReturnHash(this.shopId)
      return btoa(JSON.stringify({
        shopId: this.shopId,
        returnUrl: window.location.origin,
        app: 'pos',
        hash: currentPosUiHash() || undefined
      }))
    },
    replacePathKeepingHash (path) {
      window.history.replaceState({}, '', path + window.location.hash)
    },
    loginWithGoogle () {
      const state = this.buildSocialState()
      window.location.href = `https://${this.config.public.authDomain}/api/auth/social-login?provider=google&state=${encodeURIComponent(state)}`
    },
    loginWithMicrosoft () {
      const state = this.buildSocialState()
      window.location.href = `https://${this.config.public.authDomain}/api/auth/social-login?provider=microsoft&state=${encodeURIComponent(state)}`
    },
    async requestOTPCode () {
      this.isRequestingOTP = true
      this.loginError = ''
      try {
        await this.authClient.emailOtp.sendVerificationOtp({
          email: this.emailOTPForm.email,
          type: 'sign-in'
        })
        this.authState = 'email-otp-verify'
        this.saveOtpFlow()
      } catch (error) {
        this.loginError = this._('Invalid email or code')
      } finally {
        this.isRequestingOTP = false
      }
    },
    async verifyOTPCode () {
      this.isVerifyingOTP = true
      this.loginError = ''
      try {
        const { error } = await this.authClient.signIn.emailOtp({
          email: this.emailOTPForm.email,
          otp: this.emailOTPCode
        })
        if (error) {
          this.loginError = this._('Invalid email or code')
          return
        }
        this.clearOtpFlow()
        await this.session.refetch?.()
        await this.finishLogin()
      } catch (error) {
        this.loginError = this._('Invalid email or code')
      } finally {
        this.isVerifyingOTP = false
      }
    },
    async finishLogin (staffReason, staffReasonDetail) {
      try {
        await finishLoginTail({
          store: this.$store,
          socket: this.$socket,
          linkedStores: this.linkedStores,
          shopId: this.shopId,
          clientid: this.clientid,
          session: this.session,
          staffReason,
          staffReasonDetail
        })
        this.emailOTPForm = { email: '' }
        this.emailOTPCode = ''
        this.staffReasonForm = { reason: '', detail: '' }
        this.staffReasonPresets = []
      } catch (e) {
        if (e?.message === 'STAFF_REASON_REQUIRED') {
          markStaffReasonFlow(this.shopId)
          this.$store.dispatch('setSessionExpired', false)
          this.staffReasonPresets = e.reasonPresets || []
          this.authState = 'staff-reason'
          this.loginError = ''
          return
        }
        if (e?.message === 'POS_SESSION_EXPIRED') {
          // The cookie session is past this shop's idle limit; drop it so the
          // next sign-in creates a fresh one instead of reusing it.
          try {
            await this.authClient.signOut()
          } catch {}
          this.clearOtpFlow()
          clearStaffReasonFlow()
          this.$store.dispatch('setSessionExpired', true)
          this.authState = 'initial'
          this.loginError = ''
          return
        }
        this.showAccessDeniedError()
      }
    },
    staffReasonLabel (preset) {
      const key = preset.label || preset.id
      const translated = this._(key)
      return translated !== key ? translated : key
    },
    async submitStaffReason () {
      if (!this.staffReasonForm.reason) {
        this.loginError = this._('Please select a reason.')
        return
      }
      if (this.staffReasonForm.reason === 'other' && !this.staffReasonForm.detail.trim()) {
        this.loginError = this._('Please describe the reason for access.')
        return
      }
      this.isSubmittingStaffReason = true
      this.loginError = ''
      try {
        const detail = this.staffReasonForm.reason === 'other' ? this.staffReasonForm.detail.trim() : ''
        await this.finishLogin(this.staffReasonForm.reason, detail || undefined)
      } finally {
        this.isSubmittingStaffReason = false
      }
    },
    reloadUI () {
      if (this.shopId !== this.triedShopId) {
        window.location.href = '/' + this.customShopId
      } else {
        reloadApp()
      }
    },
    reloadApp,
    shopSettings (shopId) {
      return this.allSettings[shopId + '-' + this.locale] || {}
    },
    focusEmail () {
      if (this.$refs && this.$refs.email) {
        this.$refs.email.focus()
      }
    },
    focusShopId () {
      if (this.$refs && this.$refs.shopid) {
        this.$refs.shopid.focus()
      }
    },
    async copyClientIdToClipboard () {
      try {
        await this.$copyText(this.clientid)
        this.showNotification(this._('Client identifier copied to clipboard!'))
      } catch (e) {
        this.showError(e)
      }
    }
  },
  mounted () {
    if (typeof window !== 'undefined') {
      try {
        if (sessionStorage.getItem(SESSION_EXPIRED_STORAGE_KEY) && !isStaffReasonFlowActive(this.shopId)) {
          sessionStorage.removeItem(SESSION_EXPIRED_STORAGE_KEY)
          this.$store.dispatch('setSessionExpired', true)
        }
      } catch {
        // Ignore storage access errors.
      }
      savePosReturnHash(this.shopId)
      const params = new URLSearchParams(window.location.search)
      if (params.get('staff_access') === '1') {
        params.delete('staff_access')
        const query = params.toString()
        this.replacePathKeepingHash(window.location.pathname + (query ? '?' + query : ''))
        this.startStaffReasonFlow()
        return
      }
      if (this.hasPendingStaffReason()) {
        this.startStaffReasonFlow()
        return
      }
      if (params.get('error') === 'auth_failed') {
        this.showAccessDeniedError()
        params.delete('error')
        const query = params.toString()
        this.replacePathKeepingHash(window.location.pathname + (query ? '?' + query : ''))
      }
    }
    const restoredOtpFlow = this.restoreOtpFlow()
    if (restoredOtpFlow) {
      this.emailOTPForm = { email: restoredOtpFlow.email }
      this.authState = 'email-otp-verify'
    }

    if (!this.isValidShop) {
      this.focusShopId()
    }
  }
}
</script>
